/*	-----------------------------------------------------------------------------
  GLOBAL VARIABLES
--------------------------------------------------------------------------------- */

const easeInOut = "power4.inOut",
    easeOut = "power1.out",
    easeIn = "power1.in",
    easeNone = "none",
    duration = 0.7,
    durationSlow = 1.7,
    durationFast = 0.3,
    start = "top 90%",
    startScrub = "top bottom",
    startPin = "top top",
    scale = 1.5,
    navHeight = document.querySelector(".navigation").offsetHeight;

gsap.registerPlugin(ScrollTrigger, SplitText);

/*	-----------------------------------------------------------------------------
  FUNCTIONS
--------------------------------------------------------------------------------- */

function manualLoad(item) {
    var splitInner = new SplitText(item, {
        type: "lines",
        linesClass: "line__inner",
    });
    var splitOuter = new SplitText(item, {
        type: "lines",
        linesClass: "line__outer no-overflow",
    });

    var tl = gsap.timeline();
    tl.from(splitInner.lines, {
        duration: durationSlow,
        yPercent: 100,
        ease: easeInOut,
        stagger: 0.05,
    });
}

/*	-----------------------------------------------------------------------------
  MARQUEE ANIMATION - Simple GSAP Implementation
--------------------------------------------------------------------------------- */

/**
 * Simple GSAP Marquee Animation
 *
 * Based on industry-standard patterns using xPercent for smooth infinite loops
 * Features:
 * - Seamless infinite scrolling
 * - Responsive behavior
 * - Hover pause effects
 * - Performance optimized
 *
 * ADJUST TIMING HERE:
 * - Change `duration` value for speed (higher = slower)
 * - Change `pauseOnHover` for hover behavior
 */

// Helper function to initialize a single marquee
function initSingleMarquee(marquee) {
    const config = {
        duration: 17,
        pauseOnHover: true,
        ease: "none",
    };

    const items = marquee.querySelectorAll(".amenities__marquee-item");

    if (items.length === 0 || items.length === 1) {
        return;
    }

    // Create wrapper for seamless loop
    const wrapper = document.createElement("div");
    wrapper.className = "marquee-wrapper";
    wrapper.style.display = "flex";

    // Move original items to wrapper
    while (marquee.firstChild) {
        wrapper.appendChild(marquee.firstChild);
    }

    // Clone items for seamless loop
    const clones = Array.from(wrapper.children).map((item) =>
        item.cloneNode(true),
    );
    clones.forEach((clone) => wrapper.appendChild(clone));

    // Add wrapper back to marquee
    marquee.appendChild(wrapper);

    // Set up GSAP animation using xPercent
    const tl = gsap.timeline({ repeat: -1, ease: config.ease });

    tl.to(wrapper, {
        xPercent: -50,
        duration: config.duration,
        ease: config.ease,
    });

    // Store timeline for controls
    marquee._timeline = tl;

    // Hover effects
    if (config.pauseOnHover) {
        marquee.addEventListener("mouseenter", () => {
            gsap.to(tl, { timeScale: 0.3, duration: 0.3 });
        });

        marquee.addEventListener("mouseleave", () => {
            gsap.to(tl, { timeScale: 1, duration: 0.3 });
        });
    }
}

function initMarquees() {
    // Check if GSAP is available
    if (typeof gsap === "undefined") {
        console.error("GSAP is not loaded! Marquees cannot initialize.");
        return;
    }

    // Disable marquee on mobile (768px and below)
    if (window.innerWidth <= 768) {
        return;
    }

    // Find all marquee elements
    const marquees = document.querySelectorAll(".amenities__marquee");

    if (marquees.length === 0) {
        return;
    }

    marquees.forEach((marquee, index) => {
        initSingleMarquee(marquee);
    });

    // Handle window resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            // Check if we need to disable/enable marquee based on screen size
            if (window.innerWidth <= 768) {
                // Disable marquee on mobile
                marquees.forEach((marquee) => {
                    if (marquee._timeline) {
                        marquee._timeline.kill();
                        marquee._timeline = null;
                        // Reset to original state
                        const wrapper =
                            marquee.querySelector(".marquee-wrapper");
                        if (wrapper) {
                            const items = Array.from(wrapper.children);
                            const originalCount = Math.ceil(items.length / 2);
                            // Remove cloned items (keep only original items)
                            items
                                .slice(originalCount)
                                .forEach((item) => item.remove());
                        }
                    }
                });
            } else {
                // Re-enable marquee on desktop
                marquees.forEach((marquee) => {
                    if (!marquee._timeline) {
                        // Reinitialize marquee for this element
                        const items = marquee.querySelectorAll(
                            ".amenities__marquee-item",
                        );
                        if (items.length > 1) {
                            // Re-run marquee setup for this specific element
                            initSingleMarquee(marquee);
                        }
                    } else {
                        marquee._timeline.restart();
                    }
                });
            }
        }, 250);
    });

    // Performance: Pause when tab is hidden
    document.addEventListener("visibilitychange", () => {
        marquees.forEach((marquee) => {
            if (marquee._timeline) {
                if (document.hidden) {
                    marquee._timeline.pause();
                } else {
                    marquee._timeline.resume();
                }
            }
        });
    });
}

// Initialize marquees when DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMarquees);
} else {
    initMarquees();
}

/*	-----------------------------------------------------------------------------
  VERTICAL MARQUEE ANIMATION - Neighborhood Highlights
--------------------------------------------------------------------------------- */

// Global resize handler for vertical marquees (runs regardless of initial screen size)
function setupVerticalMarqueeResizeHandler() {
    let resizeTimeout;
    let lastWidth = window.innerWidth;

    // Prevent multiple event listeners
    if (window._verticalMarqueeResizeHandlerAttached) {
        return;
    }
    window._verticalMarqueeResizeHandlerAttached = true;

    window.addEventListener("resize", () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            const currentWidth = window.innerWidth;
            const widthDiff = Math.abs(currentWidth - lastWidth);

            // Only process significant width changes
            if (widthDiff < 50) {
                return;
            }

            const isMobile = currentWidth <= 768;
            const wasDesktop = lastWidth > 768;
            const wasMobile = lastWidth <= 768;

            const rows = document.querySelectorAll(
                ".neighborhood__highlights-row",
            );

            if (isMobile && wasDesktop) {
                // Disable marquee when switching to mobile
                rows.forEach((row) => {
                    if (row._verticalTimeline) {
                        row._verticalTimeline.kill();
                        row._verticalTimeline = null;

                        // Reset to original state
                        const wrapper = row.querySelector(
                            ".vertical-marquee-wrapper",
                        );
                        if (wrapper) {
                            const column = row.querySelector(
                                ".neighborhood__highlights-column",
                            );
                            // Move items back to column and remove wrapper
                            while (wrapper.firstChild) {
                                column.appendChild(wrapper.firstChild);
                            }
                            wrapper.remove();

                            // Remove cloned items (keep only original items)
                            const items = Array.from(column.children);
                            const originalCount = Math.ceil(items.length / 2);
                            items
                                .slice(originalCount)
                                .forEach((item) => item.remove());
                        }
                    }
                });
            } else if (!isMobile && wasMobile) {
                // Re-enable marquee when switching to desktop
                initVerticalMarqueeAnimation();
            } else if (!isMobile && wasDesktop) {
                // Just restart animations on desktop resize
                rows.forEach((row) => {
                    if (row._verticalTimeline) {
                        row._verticalTimeline.restart();
                    }
                });
            }

            lastWidth = currentWidth;
        }, 500);
    });
}

function initVerticalMarquees() {
    // Check if GSAP is available
    if (typeof gsap === "undefined") {
        console.error(
            "GSAP is not loaded! Vertical marquees cannot initialize.",
        );
        return;
    }

    // Always set up resize handler first, regardless of current screen size
    setupVerticalMarqueeResizeHandler();

    // Only initialize marquee animation on desktop (disable on mobile for performance)
    if (window.innerWidth <= 768) {
        return;
    }

    // Initialize the actual animation
    initVerticalMarqueeAnimation();

    // Performance: Pause when tab is hidden
    setupVerticalMarqueeVisibilityHandler();
}

// Function to handle the actual marquee animation setup
function initVerticalMarqueeAnimation() {
    // Configuration - ADJUST THESE VALUES
    const config = {
        duration: 25, // seconds for one complete loop (higher = slower)
        ease: "none",
    };

    // Find the neighborhood highlights container
    const container = document.querySelector(".neighborhood__highlights-items");

    if (!container) {
        return;
    }

    const rows = container.querySelectorAll(".neighborhood__highlights-row");

    if (rows.length === 0) {
        return;
    }

    rows.forEach((row, index) => {
        const column = row.querySelector(".neighborhood__highlights-column");
        const items = column.querySelectorAll(".neighborhood__highlights-item");

        if (items.length === 0) {
            return;
        }

        // Skip if already has a timeline (avoid double initialization)
        if (row._verticalTimeline) {
            return;
        }

        // Create wrapper for seamless vertical loop
        const wrapper = document.createElement("div");
        wrapper.className = "vertical-marquee-wrapper";
        wrapper.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: var(--gap-lg);
        `;

        // Move original items to wrapper
        while (column.firstChild) {
            wrapper.appendChild(column.firstChild);
        }

        // Clone items for seamless loop
        const clones = Array.from(wrapper.children).map((item) =>
            item.cloneNode(true),
        );
        clones.forEach((clone) => wrapper.appendChild(clone));

        // Add wrapper back to column
        column.appendChild(wrapper);

        // Set up GSAP vertical animation
        const direction = index === 0 ? -1 : 1; // First row up (-1), second row down (1)
        const tl = gsap.timeline({ repeat: -1, ease: config.ease });

        tl.to(wrapper, {
            yPercent: direction * 50, // Move by 50% (half the height due to duplication)
            duration: config.duration,
            ease: config.ease,
        });

        // Store timeline for controls
        row._verticalTimeline = tl;
    });
}

// Function to handle visibility changes (pause when tab hidden)
function setupVerticalMarqueeVisibilityHandler() {
    // Prevent multiple event listeners
    if (window._verticalMarqueeVisibilityHandlerAttached) {
        return;
    }
    window._verticalMarqueeVisibilityHandlerAttached = true;

    document.addEventListener("visibilitychange", () => {
        const rows = document.querySelectorAll(".neighborhood__highlights-row");
        rows.forEach((row) => {
            if (row._verticalTimeline) {
                if (document.hidden) {
                    row._verticalTimeline.pause();
                } else {
                    row._verticalTimeline.resume();
                }
            }
        });
    });
}

// Initialize vertical marquees when DOM is ready (desktop only)
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initVerticalMarquees);
} else {
    initVerticalMarquees();
}

// Global controls for debugging
window.marqueeControls = {
    pause: () => {
        document.querySelectorAll(".amenities__marquee").forEach((marquee) => {
            marquee._timeline?.pause();
        });
    },
    resume: () => {
        document.querySelectorAll(".amenities__marquee").forEach((marquee) => {
            marquee._timeline?.resume();
        });
    },
    setSpeed: (duration) => {
        document.querySelectorAll(".amenities__marquee").forEach((marquee) => {
            if (marquee._timeline) {
                marquee._timeline.duration(duration);
            }
        });
    },
    // Vertical marquee controls (desktop only)
    pauseVertical: () => {
        document
            .querySelectorAll(".neighborhood__highlights-row")
            .forEach((row) => {
                row._verticalTimeline?.pause();
            });
    },
    resumeVertical: () => {
        document
            .querySelectorAll(".neighborhood__highlights-row")
            .forEach((row) => {
                row._verticalTimeline?.resume();
            });
    },
    setVerticalSpeed: (duration) => {
        document
            .querySelectorAll(".neighborhood__highlights-row")
            .forEach((row) => {
                if (row._verticalTimeline) {
                    row._verticalTimeline.duration(duration);
                }
            });
    },
    // Debug function to check marquee status
    checkVerticalMarqueeStatus: () => {
        const rows = document.querySelectorAll(".neighborhood__highlights-row");
        const activeMarquees = Array.from(rows).filter(
            (row) => row._verticalTimeline,
        );
        console.log(`Screen width: ${window.innerWidth}px`);
        console.log(
            `Active vertical marquees: ${activeMarquees.length}/${rows.length}`,
        );
        console.log(
            `Should be active: ${window.innerWidth > 768 ? "YES" : "NO"}`,
        );
        return {
            screenWidth: window.innerWidth,
            totalRows: rows.length,
            activeMarquees: activeMarquees.length,
            shouldBeActive: window.innerWidth > 768,
        };
    },
};

/*	-----------------------------------------------------------------------------
  FORM HANDLER
--------------------------------------------------------------------------------- */
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        const form = document.getElementById("signUpForm");
        sbjs.init();
        form.addEventListener("submit", function (e) {
            e.preventDefault();
            form.classList.add("loading");
            document.querySelector("input[name='FirstSource']").value =
                sbjs.get.first.src +
                " - " +
                sbjs.get.first.mdm +
                " - " +
                sbjs.get.first.cmp +
                " - " +
                sbjs.get.first.trm;

            document.querySelector("input[name='LastSource']").value =
                sbjs.get.current.src +
                " - " +
                sbjs.get.current.mdm +
                " - " +
                sbjs.get.current.cmp +
                " - " +
                sbjs.get.current.trm;

            const incomeRadios = form.querySelectorAll('input[name="Income"]');
            let incomeSelected = false;
            incomeRadios.forEach((radio) => {
                if (radio.checked) {
                    incomeSelected = true;
                }
            });
            if (!incomeSelected) {
                document.querySelector(".form__response").innerHTML =
                    "Please select your household income.";
                return;
            }
            let formData = new FormData(this);
            let data = {};
            formData.forEach((value, key) => {
                data[key] = value;
            });
            var endpoint = "/api.php";
            var hhIncome = data["Income"];
            var email = data["Email"];
            fetch(endpoint, {
                method: "POST",
                body: formData,
            })
                .then((response) => response.text())
                .then((response) => {
                    if (response.indexOf("true") > 0) {
                        document.querySelector(".form__response").innerHTML =
                            "Thank you for your submission! We're here to help you get all required information entered correctly. <a href='https://housingconnect.nyc.gov/PublicWeb/details/7352' target='_blank' rel='noopener noreferrer'>Click here</a> to submit your application on Housing Connect and stay tuned for our follow-up.";
                        submitgTag(hhIncome);
                    } else if (response.indexOf("duplicate email") > 0) {
                        document.querySelector(".form__response").innerHTML =
                            "Thank you for your interest. " +
                            email +
                            " is already signed up.";
                    } else {
                        document.querySelector(".form__response").innerHTML =
                            "An issue has occurred. Please try again later or reach out to <a href='mailto:info@theorchardlottery.com'>info@theorchardlottery.com</a>.";
                    }
                    form.classList.remove("loading");
                })
                .catch((error) => {
                    console.log(error);
                    document.querySelector(".form__response").innerHTML =
                        "An issue has occurred. Please try again later or reach out to <a href='mailto:info@theorchardlottery.com'>info@theorchardlottery.com</a>.";
                    document
                        .querySelector("#signUpForm button")
                        .classList.remove("no-pointer");
                    form.classList.remove("loading");
                });
        });
    });

    function submitgTag(hhIncome) {
        dataLayer.push({
            event: "Sign_up",
            income_label: hhIncome,
        });
    }
})();

/*	-----------------------------------------------------------------------------
  SMOOTH SCROLL
--------------------------------------------------------------------------------- */

(function () {
    function smoothScroll() {
        const lenis = new Lenis({});

        window.lenis = lenis;

        lenis.on("scroll", (e) => {
            ScrollTrigger.update;
        });

        function raf(time) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }

        requestAnimationFrame(raf);

        gsap.ticker.add((time) => {
            lenis.raf(time * 1000);
        });

        gsap.ticker.lagSmoothing(0);
    }
    document.addEventListener("DOMContentLoaded", function () {
        smoothScroll();
    });
})();

/*	-----------------------------------------------------------------------------
    SCROLL ANIMATIONS
--------------------------------------------------------------------------------- */

(function () {
    window.addEventListener("load", function () {
        // HEADING
        const loadHeading = document.querySelectorAll(".load__heading");
        loadHeading.forEach((item) => {
            if (window.innerWidth <= 768) return;
            var splitInner = new SplitText(item, {
                type: "lines",
                linesClass: "line__inner",
            });
            var splitOuter = new SplitText(item, {
                type: "lines",
                linesClass: "line__outer no-overflow",
            });
            var tl = gsap.timeline({
                scrollTrigger: {
                    trigger: item,
                    start: item.classList.contains("--no-scroll")
                        ? "top bottom"
                        : start,
                    end: "bottom 80%",
                },
            });
            tl.from(splitInner.lines, {
                duration: durationSlow,
                yPercent: 100,
                ease: easeInOut,
                stagger: 0.05,
            });
        });

        // TEXT
        const loadText = document.querySelectorAll(".load__text");
        loadText.forEach((item) => {
            var tl = gsap.timeline({
                scrollTrigger: {
                    trigger: item,
                    start: item.classList.contains("--no-scroll")
                        ? "top bottom"
                        : start,
                    end: "bottom 85%",
                },
            });
            tl.from(item, {
                duration: durationSlow,
                opacity: 0,
                ease: easeInOut,
            });
        });

        // IMAGES
        const loadImages = document.querySelectorAll(".load__image");
        loadImages.forEach((item) => {
            var tl = gsap.timeline({
                scrollTrigger: {
                    trigger: item,
                    start: start,
                    end: "bottom 85%",
                },
            });
            tl.from(item, {
                duration: durationSlow,
                opacity: 0,
                ease: easeInOut,
            });
        });

        const loadImagesScale = document.querySelectorAll(
            ".load__image--scale",
        );
        loadImagesScale.forEach((item) => {
            var tl = gsap.timeline({
                scrollTrigger: {
                    trigger: item,
                    start: start,
                    end: "bottom 80%",
                },
            });
            tl.from(item, {
                duration: durationSlow,
                scale: 1.2,
                ease: easeInOut,
            });
        });
    });
})();

/*	-----------------------------------------------------------------------------
    SLIDERS
--------------------------------------------------------------------------------- */

(function () {
    window.addEventListener("load", function () {
        // Residences Swiper with custom pagination
        const residencesSwiper = new Swiper(".residences .swiper", {
            slidesPerView: 1,
            loop: false,
            speed: 800,
            spaceBetween: 0,
            navigation: {
                nextEl: ".residences .swiper-button-next",
                prevEl: ".residences .swiper-button-prev",
            },
            on: {
                init: function () {
                    updateResidencesPagination(this);
                },
                slideChange: function () {
                    updateResidencesPagination(this);
                },
            },
        });

        function updateResidencesPagination(swiper) {
            const currentSlide = swiper.realIndex + 1;
            const totalSlides = swiper.slides.length;

            // Update counter
            const currentElement = document.querySelector(
                ".residences-pagination__current",
            );
            if (currentElement) {
                currentElement.textContent = currentSlide
                    .toString()
                    .padStart(2, "0");
            }

            // Update progress bar
            const progressFill = document.querySelector(
                ".residences-pagination__progress-fill",
            );
            if (progressFill) {
                const progressPercentage = (currentSlide / totalSlides) * 100;
                progressFill.style.width = progressPercentage + "%";
            }
        }

        // Pricing Swiper (for floor plans)
        const pricingSwipers = document.querySelectorAll(".pricing .swiper");
        pricingSwipers.forEach(function (swiperEl) {
            new Swiper(swiperEl, {
                slidesPerView: 1,
                loop: false,
                speed: 600,
                spaceBetween: 20,
                navigation: {
                    nextEl: swiperEl.querySelector(".swiper-button-next"),
                    prevEl: swiperEl.querySelector(".swiper-button-prev"),
                },
            });
        });

        // Residences Tooltips
        const residencesTooltips = document.querySelectorAll(
            ".residences__tooltip",
        );
        residencesTooltips.forEach((tooltip) => {
            tooltip.addEventListener("click", function () {
                // On mobile, only allow one tooltip to be active at a time
                if (window.innerWidth <= 768) {
                    const isCurrentlyActive =
                        tooltip.classList.contains("active");

                    // Close all tooltips first
                    residencesTooltips.forEach((otherTooltip) => {
                        otherTooltip.classList.remove("active");
                    });

                    // If the clicked tooltip wasn't active, open it
                    if (!isCurrentlyActive) {
                        tooltip.classList.add("active");
                    }
                } else {
                    // On desktop, allow multiple tooltips (original behavior)
                    tooltip.classList.toggle("active");
                }
            });
        });
    });
})();

/*	-----------------------------------------------------------------------------
    SECTION ANIMATIONS
--------------------------------------------------------------------------------- */

// Hero
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        const countdownEl = document.querySelector(".hero__countdown");
        if (!countdownEl) return;

        const openDateUTC = new Date(countdownEl.dataset.openDate);
        const closeDate = new Date(countdownEl.dataset.closeDate);

        function updateCountdown() {
            const currentDate = new Date();

            const month = openDateUTC.getMonth() + 1;
            const day = openDateUTC.getDate();
            const formattedOpenDate = `${month}/${day}`;

            // Check if before opening date
            if (currentDate < openDateUTC) {
                countdownEl.innerHTML = `
                    <p class="hero__countdown-title text__size-5">Applications Opening on ${formattedOpenDate}</p>
                `;
                return;
            }

            if (currentDate > closeDate) {
                countdownEl.innerHTML = `
                    <p class="hero__countdown-title text__size-5">Applications Closed</p>
                `;
                return;
            }

            const diffMs = closeDate - currentDate;

            let months = 0;
            let tempDate = new Date(currentDate);

            while (true) {
                const nextMonth = new Date(tempDate);
                nextMonth.setMonth(nextMonth.getMonth() + 1);

                if (nextMonth <= closeDate) {
                    months++;
                    tempDate = nextMonth;
                } else {
                    break;
                }
            }

            const remainingMs = closeDate - tempDate;

            const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
            const weeks = Math.floor(days / 7);
            const remainingDays = days % 7;
            const hours = Math.floor(
                (remainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
            );

            countdownEl.innerHTML = `
                <p class="hero__countdown-title text__size-4">Applications Closing In</p>
                <div class="hero__countdown-grid">
                    <div class="hero__countdown-item">
                        <div class="hero__countdown-number text__size-2">${String(months).padStart(2, "0")}</div>
                        <div class="hero__countdown-label text__size-5">Month${months === 1 ? "" : "s"}</div>
                    </div>
                    <div class="hero__countdown-item">
                        <div class="hero__countdown-number text__size-2">${String(weeks).padStart(2, "0")}</div>
                        <div class="hero__countdown-label text__size-5">Week${weeks === 1 ? "" : "s"}</div>
                    </div>
                    <div class="hero__countdown-item">
                        <div class="hero__countdown-number text__size-2">${String(remainingDays).padStart(2, "0")}</div>
                        <div class="hero__countdown-label text__size-5">Day${remainingDays === 1 ? "" : "s"}</div>
                    </div>
                    <div class="hero__countdown-item">
                        <div class="hero__countdown-number text__size-2">${String(hours).padStart(2, "0")}</div>
                        <div class="hero__countdown-label text__size-5">Hour${hours === 1 ? "" : "s"}</div>
                    </div>
                </div>
            `;
        }

        updateCountdown();
        setInterval(updateCountdown, 60000);

        const container = document.querySelector(".hero__container");
        const heroBottom = document.querySelector(".hero__bottom");
        gsap.set(heroBottom, {
            height: 0,
        });
        gsap.to(heroBottom, {
            height: "auto",
            scrollTrigger: {
                trigger: container,
                start: "top top",
            },
        });
    });
})();

// Amenities
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        const amenitiesList = document.querySelectorAll(
            ".amenities__list-item",
        );

        // Desktop hover behavior
        if (window.innerWidth > 768) {
            amenitiesList.forEach((item) => {
                item.addEventListener("mouseenter", function () {
                    amenitiesList.forEach((item) => {
                        item.classList.remove("active");
                    });
                    item.classList.add("active");
                });
            });
        }

        // Function to initialize amenities swiper
        function initAmenitiesSwiper() {
            if (
                window.innerWidth <= 768 &&
                !document.querySelector(".amenities-swiper .swiper-initialized")
            ) {
                const amenitiesSwiper = new Swiper(".amenities-swiper", {
                    slidesPerView: 1,
                    loop: false,
                    pagination: {
                        el: ".amenities-pagination",
                        clickable: true,
                        bulletClass: "amenities-bullet",
                        bulletActiveClass: "amenities-bullet-active",
                    },
                    navigation: {
                        nextEl: ".amenities-next",
                        prevEl: ".amenities-prev",
                    },
                    on: {
                        slideChange: function () {
                            // Update active state for slides
                            const slides = document.querySelectorAll(
                                ".amenities__list-item",
                            );
                            slides.forEach((slide, index) => {
                                slide.classList.toggle(
                                    "active",
                                    index === this.activeIndex,
                                );
                            });
                        },
                    },
                });
                return amenitiesSwiper;
            }
            return null;
        }

        // Initialize swiper on mobile
        initAmenitiesSwiper();

        // Handle window resize with proper reinitialization
        let resizeTimeout;
        let lastWidth = window.innerWidth;

        window.addEventListener("resize", () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const currentWidth = window.innerWidth;
                const widthDiff = Math.abs(currentWidth - lastWidth);

                // Only reinitialize if there's a significant width change (more than 50px)
                // This prevents mobile browser UI changes from triggering reinitialization
                if (widthDiff < 50) {
                    return;
                }

                const isMobile = currentWidth <= 768;
                const wasDesktop = lastWidth > 768;
                const wasMobile = lastWidth <= 768;
                const swiperExists = document.querySelector(
                    ".amenities-swiper .swiper-initialized",
                );

                // Only reinitialize when crossing the mobile/desktop boundary
                if ((isMobile && wasDesktop) || (!isMobile && wasMobile)) {
                    if (swiperExists && !isMobile) {
                        // Destroy swiper when going to desktop
                        const swiperInstance =
                            document.querySelector(".amenities-swiper").swiper;
                        if (swiperInstance) {
                            swiperInstance.destroy(true, true);
                        }
                    } else if (!swiperExists && isMobile) {
                        // Initialize swiper when going to mobile
                        initAmenitiesSwiper();
                    }
                }

                lastWidth = currentWidth;
            }, 500); // Increased timeout for better debouncing
        });
    });
})();

// Residences - Now handled by Swiper in the SLIDERS section above

// Residences Mobile Expand Description
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        const expandButton = document.querySelector(
            ".residences__expand-button",
        );
        const description = document.querySelector(".residences__description");

        if (expandButton && description) {
            expandButton.addEventListener("click", function () {
                const isExpanded = expandButton.classList.contains("expanded");

                if (isExpanded) {
                    // Collapse
                    expandButton.classList.remove("expanded");
                    description.classList.remove("expanded");
                } else {
                    // Expand
                    expandButton.classList.add("expanded");
                    description.classList.add("expanded");
                }
            });

            // Handle window resize to reset state on desktop
            window.addEventListener("resize", function () {
                if (window.innerWidth > 768) {
                    expandButton.classList.remove("expanded");
                    description.classList.remove("expanded");
                }
            });
        }
    });
})();

// Amenities Mobile Expand Description
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        const expandButton = document.querySelector(
            ".amenities__expand-button",
        );
        const descriptionWrapper = document.querySelector(
            ".amenities__description-wrapper",
        );

        if (expandButton && descriptionWrapper) {
            expandButton.addEventListener("click", function () {
                const isExpanded = expandButton.classList.contains("expanded");

                if (isExpanded) {
                    // Collapse
                    expandButton.classList.remove("expanded");
                    descriptionWrapper.classList.remove("expanded");
                } else {
                    // Expand
                    expandButton.classList.add("expanded");
                    descriptionWrapper.classList.add("expanded");
                }
            });

            // Handle window resize to reset state on desktop
            window.addEventListener("resize", function () {
                if (window.innerWidth > 768) {
                    expandButton.classList.remove("expanded");
                    descriptionWrapper.classList.remove("expanded");
                }
            });
        }
    });
})();

// Developer Mobile Expand Description
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        const expandButton = document.querySelector(
            ".developer__expand-button",
        );
        const description = document.querySelector(
            ".developer__introduction-description",
        );

        if (expandButton && description) {
            expandButton.addEventListener("click", function () {
                const isExpanded = expandButton.classList.contains("expanded");

                if (isExpanded) {
                    // Collapse
                    expandButton.classList.remove("expanded");
                    description.classList.remove("expanded");
                } else {
                    // Expand
                    expandButton.classList.add("expanded");
                    description.classList.add("expanded");
                }
            });

            // Handle window resize to reset state on desktop
            window.addEventListener("resize", function () {
                if (window.innerWidth > 768) {
                    expandButton.classList.remove("expanded");
                    description.classList.remove("expanded");
                }
            });
        }
    });
})();

// Neighborhood
(function () {
    window.addEventListener("load", function () {
        const container = document.querySelector(".neighborhood");

        if (container) {
            const neighborhoodImages = document.querySelectorAll(
                    ".neighborhood__item-image",
                ),
                neighborhoodContent = document.querySelectorAll(
                    ".neighborhood__item-text:nth-child(3n + 1)",
                );

            gsap.utils.toArray(neighborhoodContent).forEach((content, i) => {
                ScrollTrigger.create({
                    trigger: content,
                    start: "top 50%",
                    end: "bottom 50%",
                    toggleClass: {
                        targets: neighborhoodImages[i],
                        className: "active",
                    },
                    onEnter: () => {
                        neighborhoodImages[i].classList.add("active");
                    },
                    onLeave: () => {
                        if (i === neighborhoodContent.length - 1) {
                            neighborhoodImages[i].classList.add("active");
                        } else {
                            neighborhoodImages[i].classList.remove("active");
                        }
                    },
                    onEnterBack: () => {
                        neighborhoodImages[i].classList.add("active");
                    },
                    onLeaveBack: () => {
                        if (i === 0) {
                            neighborhoodImages[i].classList.add("active");
                        } else {
                            neighborhoodImages[i].classList.remove("active");
                        }
                    },
                });
            });
        }
    });
})();

// Pricing
(function () {
    window.addEventListener("load", function () {
        const container = document.querySelector(".pricing");

        if (container) {
            const pricingToggles = document.querySelectorAll(
                    ".pricing__charts-toggles li",
                ),
                pricingTables = document.querySelectorAll(
                    ".pricing__charts-image",
                );

            pricingToggles.forEach((toggle, i) => {
                toggle.addEventListener("click", function () {
                    pricingToggles.forEach((toggle) => {
                        toggle.classList.remove("active");
                    });
                    pricingTables.forEach((table) => {
                        table.classList.remove("active");
                    });
                    pricingTables[i].classList.add("active");
                    toggle.classList.add("active");
                });
            });
            var fpToggle = document.querySelectorAll(".fp-toggle");

            fpToggle.forEach((toggle, i) => {
                toggle.addEventListener("click", function (e) {
                    e.preventDefault();
                    toggle.classList.toggle("active");
                    let nextElement =
                        toggle.closest(".unit").nextElementSibling;
                    while (nextElement) {
                        if (nextElement.classList.contains("floor-plan")) {
                            break;
                        }
                        nextElement = nextElement.nextElementSibling;
                    }
                    nextElement.classList.toggle("active");
                    setTimeout(() => {
                        ScrollTrigger.refresh();
                    }, 500);
                });
            });
        }
    });
})();

// FAQ
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        if (document.body.classList.contains("faq")) {
            document
                .querySelectorAll(".faq__section-question")
                .forEach((toggle) => {
                    toggle.addEventListener("click", function () {
                        toggle
                            .closest(".faq__section-item")
                            .classList.toggle("active");
                        setTimeout(() => {
                            ScrollTrigger.refresh();
                        }, 450);
                    });
                });

            document
                .querySelectorAll(".faq__section-group")
                .forEach((group, i) => {
                    var target = document.querySelectorAll(
                        ".faq__section-anchor",
                    )[i];
                    ScrollTrigger.create({
                        trigger: group,
                        start: "top 30%",
                        end: "bottom 30%",
                        toggleClass: { targets: target, className: "active" },
                    });
                });

            // Handle hash anchor for specific FAQ items (e.g., #calculator)
            const hash = window.location.hash;
            if (hash) {
                const targetItem = document.querySelector(hash + ".faq__section-item");
                if (targetItem) {
                    // Expand the FAQ item
                    targetItem.classList.add("active");
                    // Scroll to the item after a short delay to allow page to render
                    setTimeout(() => {
                        targetItem.scrollIntoView({ behavior: "smooth", block: "center" });
                        ScrollTrigger.refresh();
                    }, 100);
                }
            }
        }
    });
})();

/*	-----------------------------------------------------------------------------
  NAVIGATION
--------------------------------------------------------------------------------- */

(function () {
    window.addEventListener("load", function () {
        const navLinks = document.querySelectorAll(".navigation__link");
        document.body.style.setProperty("--nav-height", navHeight + "px");

        navLinks.forEach((link) => {
            const targetId = link.getAttribute("data-attribute-anchor");
            const targetSection = document.getElementById(targetId);

            if (targetSection) {
                ScrollTrigger.create({
                    trigger: targetSection,
                    start: "top center",
                    end: "bottom center",
                    toggleClass: { targets: link, className: "active" },
                });
                setTimeout(() => {
                    link.addEventListener("click", function (e) {
                        e.preventDefault();
                        window.history.pushState({}, "", "#" + targetId);
                        gsap.to(window, {
                            duration: 0,
                            ease: easeInOut,
                            scrollTo: {
                                y: targetSection,
                                offsetY: navHeight,
                            },
                        });
                    });
                }, 1000);
            }
        });
        ScrollTrigger.create({
            trigger: document.body,
            start: "5px top",
            end: "+=99999px",
            toggleClass: { targets: "body", className: "navigation--active" },
        });
        document.querySelectorAll(".navigation a").forEach((link) => {
            link.addEventListener("click", function () {
                document.body.classList.remove("init__nav");
            });
        });
        document.querySelectorAll(".navigation__toggle").forEach((toggle) => {
            toggle.addEventListener("click", function () {
                document.body.classList.toggle("init__nav");
            });
        });
    });
})();

/*	-----------------------------------------------------------------------------
  MODAL
--------------------------------------------------------------------------------- */
(function () {
    document.addEventListener("DOMContentLoaded", function () {
        const buttons = document.querySelectorAll(".open-modal"),
            modal = document.querySelector("#modal"),
            modalContainer = document.querySelector(".modal__container"),
            modalContent = document.querySelector(".modal__content"),
            tl = gsap.timeline({
                paused: true,
            });

        tl.add(() => {
            modal.style.pointerEvents = "none";
            modalContent.style.pointerEvents = "none";
        });
        tl.to(
            modal,
            {
                opacity: 1,
                duration: 0.3,
                ease: easeOut,
            },
            0,
        );
        tl.to(
            modalContainer,
            {
                y: 0,
                duration: 0.4,
                ease: easeOut,
            },
            0.1,
        );
        tl.add(() => {
            modal.style.pointerEvents = "initial";
            modalContent.style.pointerEvents = "initial";
        });

        buttons.forEach((button) => {
            button.addEventListener("click", function (e) {
                e.preventDefault();
                button.getAttribute("data-attribute-modal") === "amenities"
                    ? document.body.classList.add("init-amenities-modal")
                    : null;
                tl.play();
            });
        });

        modal.addEventListener("click", function (e) {
            if (
                e.target === modal ||
                e.target.classList.contains("modal__close")
            ) {
                tl.reverse();
                setTimeout(() => {
                    document.body.classList.remove("init-amenities-modal");
                }, 1000);
            }
        });
    });
})();

/*	-----------------------------------------------------------------------------
  LOAD FUNCTIONS
--------------------------------------------------------------------------------- */

(function () {
    window.addEventListener("load", function () {
        document.body.classList.remove("loading");
    });
})();

/*	-----------------------------------------------------------------------------
  RESIDENCES TABLE TOGGLE (Mobile Accordion)
--------------------------------------------------------------------------------- */

(function () {
    // Select all table headers
    const tableHeaders = document.querySelectorAll(".residences__table-header");

    tableHeaders.forEach((header) => {
        header.addEventListener("click", function () {
            // Only run on mobile (check window width)
            if (window.innerWidth > 768) return;

            // Close all other open sections first
            tableHeaders.forEach((otherHeader) => {
                if (otherHeader !== this) {
                    otherHeader.classList.remove("active");
                    const otherContent = otherHeader.nextElementSibling;
                    if (
                        otherContent &&
                        otherContent.classList.contains(
                            "residences__table-content",
                        )
                    ) {
                        otherContent.classList.remove("show");
                    }
                }
            });

            // Toggle active class on clicked header
            this.classList.toggle("active");

            // Find the next sibling content wrapper
            const contentWrapper = this.nextElementSibling;

            if (
                contentWrapper &&
                contentWrapper.classList.contains("residences__table-content")
            ) {
                contentWrapper.classList.toggle("show");
            }
        });
    });
})();
